/* Copyright (c) 2022 MOCA Systems, Inc. All rights reserved. */

import { Injectable, OnDestroy } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { AbstractFormManager } from 'src/app/common/form-utils/abstract-form-manager.service';
import { dateRangeStringValidator } from 'src/app/common/form-utils/validators';
import { reportsFormTypeConfig } from '../components/reportsFormTypeConfig';
import { IReportOptions } from '../models/ReportOptions';
import { ExportFormatType } from '../types/ExportFormatType';
import { ReportFormControl } from '../types/ReportFormControl';
import { ReportType } from '../types/ReportType';
import { DEFAULT_REPORT_FORM_VALUES, REPORT_FORM_CONTROLS } from '../models/report-form.constants';

/**
 * Creates and manages a multi-type form similar to [[TicketFormManager]], built on the same general principles
 * - provide as a component service `@Component{...blahblah, providers: [AbstractFormManager]}`
 * - create to get access to the formGroup in the component that needs it. Consider it readonly outside that purpose
 * - use the various methods on this service to manipulate it
 * - it's also _likely_ that this will diverge from TicketFormManager in the sense that in this case the formGroup wants to be
 * the primary source of data, _not_ the model [[IReportOptions]]
 */
@Injectable()
export class ReportsFormManager
	extends AbstractFormManager<IReportOptions, ReportType, ReportFormControl>
	implements OnDestroy
{
	private shutdownSubject = new Subject<void>();
	public setOfCustomFieldControls = [
		ReportFormControl.CustomField1,
		ReportFormControl.CustomField2,
		ReportFormControl.CustomField3,
	];
	protected formTypeConfig = reportsFormTypeConfig;

	protected getControlDataFromModel(controlName: ReportFormControl, model: IReportOptions) {
		return model[controlName];
	}

	// consider value of some utility that takes an object, then generates a formGroup from
	// the name/ type of the properties, decorator probably
	protected createFormGroup() {
		return this.formBuilder.group(REPORT_FORM_CONTROLS, {
			validators: [dateRangeStringValidator('asOfDate', 'endDate')],
		});
	}

	constructor(protected override formBuilder: UntypedFormBuilder) {
		super(formBuilder);
	}

	public override create(reportType: ReportType, reportOptions: IReportOptions, isDisabled?: boolean) {
		const formGroup = super.create(reportType, reportOptions, isDisabled); //do first

		//temp sanity thing, investigation will be in the subsequent ticket
		if (reportOptions && reportOptions.reportType !== reportType) {
			throw new Error('inconsistent types');
		}

		this.startTypeChangeListener();
		this.customFieldsFiltersChangeListener();
		this.exportFormatTypeChangeListener();
		this.includeWBSCodeFilterChangeListener();

		return formGroup;
	}

	public override patchForm(reportOptions: IReportOptions) {
		super.patchForm(reportOptions);
		// do not use the 'emitEvent' option as false to prevent it from breaking.
		this.formGroup.patchValue(reportOptions);
	}

	// internally monitor the form for type changes, and reset the rest of the form when it does
	// - may switch this internal monitoring to a public method for setting it
	private startTypeChangeListener() {
		this.valueChanges
			.pipe(
				filter((emission) => emission[ReportFormControl.ReportType] !== undefined),
				map((emission) => emission[ReportFormControl.ReportType]),
				distinctUntilChanged(),
				takeUntil(this.shutdownSubject)
			)
			.subscribe((type) => this.changeFormType(type as ReportType));
	}

	private customFieldsFiltersChangeListener() {
		this.activeControl$
			.pipe(
				switchMap(() => this.valueChangesFor(ReportFormControl.FilterByCustomFieldsToggle)),
				takeUntil(this.shutdownSubject)
			)
			.subscribe((isFilterByCustomFields) => this.toggleCustomFieldsControls(isFilterByCustomFields));
	}

	private toggleCustomFieldsControls(isFilterByCustomFields: boolean) {
		if (isFilterByCustomFields) {
			this.setOfCustomFieldControls.forEach((control: ReportFormControl, index: number) => {
				this.addFormControl(control, index !== this.setOfCustomFieldControls.length - 1, true);
			});
		} else {
			this.setOfCustomFieldControls.forEach((control: ReportFormControl, index: number) => {
				this.resetControlToDefault(control);
				this.removeFormControl(control, index !== this.setOfCustomFieldControls.length - 1);
			});
		}
	}

	// a listener on the ExportFormatType, which adds and removes the ExportFormatTemplate control based on the result
	private exportFormatTypeChangeListener() {
		this.valueChangesFor(ReportFormControl.ExportFormatType)
			.pipe(takeUntil(this.shutdownSubject))
			.subscribe((isExportFormatType) => {
				isExportFormatType === ExportFormatType.P6Excel
					? this.addFormControl(ReportFormControl.ExportFormatTemplate)
					: this.removeFormControl(ReportFormControl.ExportFormatTemplate);
			});
	}

	private includeWBSCodeFilterChangeListener() {
		this.activeControl$
			.pipe(
				switchMap(() => this.valueChangesFor(ReportFormControl.IncludeWBSCodeFilterToggle)),
				takeUntil(this.shutdownSubject)
			)
			.subscribe((isIncludeWBSCodeFilter) => this.toggleWbsCodeFilterConfigControl(isIncludeWBSCodeFilter));
	}

	private toggleWbsCodeFilterConfigControl(isIncludeWBSCodeFilter: boolean) {
		if (isIncludeWBSCodeFilter) {
			this.addFormControl(ReportFormControl.WbsCodeFilterConfig);
		} else {
			this.resetControlToDefault(ReportFormControl.WbsCodeFilterConfig);
			this.removeFormControl(ReportFormControl.WbsCodeFilterConfig);
		}
	}

	/**
	 * Resets a specified form control to its default value.
	 *
	 * This method sets the value of a given form control within the form group
	 * to its predefined default as specified in `DEFAULT_FORM_VALUES`.
	 *
	 * Note: Since `setValue` is used, this operation will trigger the `valueChanges`
	 * observable for the control, which has a subscription that saves the new value
	 * to the report preferences (see the component). Resetting a control immediately
	 * updates the preferences with the default value.
	 *
	 * @param control - The form control to reset.
	 */
	private resetControlToDefault(control: ReportFormControl) {
		const formControl = this.formGroup.get(control);
		if (formControl) {
			formControl.reset(DEFAULT_REPORT_FORM_VALUES[control]);
			// patch the internal model in the abstract class to keep the form group
			// and the internal model in sync
			this.patchFormPartial({
				[control as string]: DEFAULT_REPORT_FORM_VALUES[control],
			});
		}
	}

	public override ngOnDestroy() {
		this.shutdownSubject.next();
		this.shutdownSubject.complete();
		super.ngOnDestroy();
	}
}
